import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Scope, TourDetailInterface } from '../../../../core/models';
import { animate, style, transition, trigger } from '@angular/animations';
import { LoadingBarrierService } from '../../../../services/resources/loading-barrier.service';
import { v7 as uuidv7 } from 'uuid';

@Component({
  selector: 'app-guide-details-tour',
  imports: [CommonModule, ReactiveFormsModule],
  standalone: true,
  template: `
    <div
      class="flex flex-col xl:flex-row gap-y-4 xl:gap-y-0 xl:gap-x-6 w-full"
      [formGroup]="tour"
    >
      <!-- Tour Dropdown -->
      <div class="flex flex-col w-full xl:w-auto" formGroupName="tourInfo">
        <label for="tourDropdown" class="mb-2 text-sm font-medium text-gray-700"
          >Tour</label
        >
        <div class="relative inline-block text-left">
          <div>
            <div class="relative">
              <input
                #comboInput
                formControlName="tourName"
                id="combobox"
                type="text"
                [value]="selectedTour?.tourName"
                (input)="onSearch($event)"
                class="text-sm sm:text-xs rounded-md border border-gray-300 py-2 px-3 pr-8 text-gray-700 focus:border-columbus-blue-500 focus:outline-none focus:ring-1 focus:ring-columbus-blue-500 w-full xl:w-auto"
                role="combobox"
                aria-controls="options"
                aria-expanded="false"
                autocomplete="off"
                [placeholder]="'Tour...'"
              />
              <button
                type="button"
                (click)="toggleTourMenu()"
                class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
              >
                <svg
                  class="h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>

              <ul
                *ngIf="isTourMenu"
                class="absolute z-10 mt-1 max-h-60 !w-full xl:!w-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-xs"
                id="options"
                role="listbox"
              >
                <li
                  *ngFor="let tour of filteredTours"
                  (click)="setTourInfo(tour)"
                  class="hover:text-white hover:bg-columbus-blue-600 active:bg-columbus-blue-700 active:text-white relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900"
                  id="option-0"
                  (keydown.enter)="setTourInfoKey($event, tour)"
                  role="option"
                  tabindex="0"
                >
                  <span class="block">{{ tour.tourName }}</span>

                  <span
                    *ngIf="selectedTour && selectedTour.id == tour.id"
                    [ngClass]="{
                      'text-columbus-blue-600': selectedTour.id === tour.id,
                    }"
                    class="absolute inset-y-0 right-0 flex items-center pr-4"
                  >
                    <svg
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </li>

                <!-- More items... -->
              </ul>
            </div>
            <p
              class="mt-1 text-xxs text-red-600"
              *ngIf="tour.invalid && (tour.dirty || tour.touched)"
            >
              {{
                tour.hasError('required')
                  ? 'Bitte Tour wählen.'
                  : 'Bitte wähle eine Tour aus der Liste.'
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col w-full xl:w-auto">
        <label class="mb-2 text-sm font-medium text-gray-700"
          >Tour Sprachen</label
        >

        <div class="space-y-2">
          <div
            *ngFor="let row of languageRowsCache"
            class="flex flex-wrap gap-x-4 gap-y-2"
          >
            <div
              *ngFor="let langControl of row; let i = index"
              class="flex items-center"
            >
              <label class="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  [formControl]="controlStatus(langControl)"
                  class="h-4 w-4 rounded border-gray-300 checked:bg-columbus-blue-600 checked:border-columbus-blue-600"
                />
                <span class="ml-2 text-sm text-gray-900">
                  {{ controlName(langControl) }}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-center xl:items-center w-full xl:w-auto">
        <span class="text-sm pb-2">
          <span class="font-medium text-gray-700">Tour bestätigt?</span>
        </span>
        <button
          type="button"
          (click)="toggleTourConfirm()"
          [ngClass]="{
            'bg-columbus-blue-600': tourConfirm.value,
            'bg-gray-200': !tourConfirm.value,
          }"
          class="bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-columbus-blue-600 focus:ring-offset-2"
          role="switch"
        >
          <span
            aria-hidden="true"
            [ngClass]="{
              'translate-x-5': tourConfirm.value,
              'translate-x-0': !tourConfirm.value,
            }"
            class="translate-x-0 pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
          ></span>
        </button>
      </div>
    </div>
  `,
  styles: ``,
  animations: [
    trigger('opacityScale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(.95)' }),
        animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('75ms ease-in', style({ opacity: 0, transform: 'scale(.95)' })),
      ]),
    ]),
  ],
})
export class GuideDetailsTourComponent implements OnInit {
  @ViewChild('comboInput', { static: true })
  comboInput!: ElementRef<HTMLInputElement>;

  @Input({ required: true }) tour!: FormGroup;
  @Input({ required: true }) tourSource!: TourDetailInterface[];

  @Input({ required: true }) languages!: FormArray;

  isTourMenu = false;

  selectedTour: TourDetailInterface | null = null;
  searchQuery: string = '';
  languageRowsCache: AbstractControl[][] = [];

  inputWidth = 'auto';
  private readonly barrier = inject(LoadingBarrierService);
  private readonly readKey = 'guide-tour-' + uuidv7();
  editorScope: Scope = 'editor';

  ngOnInit(): void {
    this.barrier.register(this.editorScope, this.readKey);
    this.tour.addValidators(this.tourIdExistsValidator());
    this.getSortedArry();
    this.computeLanguageRows();
    if (this.tourName) {
      this.findTourByName(this.tourName.toString());
    }

    this.barrier.markReady(this.editorScope, this.readKey);
  }

  private computeLanguageRows(): void {
    const ctrls = this.languages.controls;
    const n = ctrls.length;
    if (n <= 4) {
      this.languageRowsCache = [ctrls];
    } else {
      const half = Math.ceil(n / 2);
      this.languageRowsCache = [ctrls.slice(0, half), ctrls.slice(half)];
    }
  }

  findTourByName(tourName: string) {
    const foundTour = this.tourSource.find((tour) => {
      return (
        tour.tourName.trim().toLowerCase() === tourName.trim().toLowerCase()
      );
    });
    if (foundTour) {
      this.setTourInfo(foundTour);
    }
  }

  controlName(control: AbstractControl) {
    return control.get('name')?.value as string;
  }

  toggleTourMenu() {
    this.isTourMenu = !this.isTourMenu;
  }

  getSortedArry() {
    if (!this.languages.controls.length) return;

    // Original controls referenzieren – KEIN cloneDeep!
    // FormControls dürfen nicht deep-cloned werden, das zerstört
    // parent-references und ChangeDetector-Bindings.
    const sortable = this.languages.controls.map((control, index) => ({
      control,
      name: this.controlName(control),
      originalIndex: index,
    }));

    sortable.sort((a, b) => a.name.localeCompare(b.name));

    // Wenn bereits sortiert, no-op
    const alreadySorted = sortable.every((s, i) => s.originalIndex === i);
    if (alreadySorted) return;

    // FormArray neu aufbauen mit den ORIGINAL controls
    while (this.languages.length > 0) {
      this.languages.removeAt(0);
    }
    sortable.forEach((s) => this.languages.push(s.control));
  }

  controlStatus(control: AbstractControl) {
    return control.get('isActive') as FormControl;
  }

  toggleTourConfirm() {
    this.tourConfirm.setValue(!this.tourConfirm.value);
  }

  get filteredTours(): TourDetailInterface[] {
    if (!this.searchQuery) return this.tourSource;
    return this.tourSource.filter((tour) =>
      tour.tourName.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  setTourInfo(tour: TourDetailInterface) {
    this.selectedTour = tour;
    this.searchQuery = '';
    this.tourInfo.patchValue(tour);
    if (this.isTourMenu) {
      this.toggleTourMenu();
    }
  }

  setTourInfoKey(event: Event, tour: TourDetailInterface) {
    const keyEvent = event as KeyboardEvent;

    if (keyEvent.code === 'Enter') {
      this.setTourInfo(tour);
    }
  }
  onSearch(event: Event) {
    const target = event.target as HTMLInputElement;

    this.searchQuery = target.value;
    this.isTourMenu = true;
  }

  get tourInfo() {
    return this.tour.get('tourInfo') as FormGroup;
  }

  get tourName() {
    return this.tourInfo?.get('tourName')?.value as FormControl<string>;
  }

  get tourConfirm() {
    return this.tour.get('tourConfirm') as FormControl;
  }

  // in your component
  compareById(a: TourDetailInterface, b: TourDetailInterface): boolean {
    return a?.tourName === b?.tourName;
  }

  private tourIdExistsValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const id = group.get('tourInfo')?.get('id')?.value as string | null;
      const name = group.get('tourInfo')?.get('tourName')?.value as
        | string
        | null;
      if (!id || !name) return { invalidTour: true };
      return this.tourSource.some((t) => t.id === id && t.tourName === name)
        ? null
        : { invalidTour: true };
    };
  }
}
